import { z } from 'zod';
import { Id } from '../primitives.js';

/**
 * CRM: автоматические кампании и поручения сотрудникам.
 *
 * `CrmTask` — одна сущность на два смысла («у сотрудника один список дел, а
 * не два», докстрока модуля): CRM-повод по пациенту заводит кампания
 * (`kind='patient'`), поручение — человек (`kind='internal'`). Изменение
 * задачи в очереди устройства — тем же приёмом, что у наряда лаборатории:
 * дискриминированное тело на все действия хода.
 *
 * Документация модуля: docs/03-modules/08-crm.md
 */

export const CRM_TASK_KIND = ['patient', 'internal'] as const;
export const CrmTaskKind = z.enum(CRM_TASK_KIND);
export type CrmTaskKind = z.infer<typeof CrmTaskKind>;

export const CRM_TASK_STATE = ['open', 'in_progress', 'done', 'accepted', 'cancelled'] as const;
export const CrmTaskState = z.enum(CRM_TASK_STATE);
export type CrmTaskState = z.infer<typeof CrmTaskState>;

export const CRM_TASK_RESULT_ACTION = ['progress', 'done', 'accepted', 'rework', 'cancelled'] as const;
export const CrmTaskResultAction = z.enum(CRM_TASK_RESULT_ACTION);
export type CrmTaskResultAction = z.infer<typeof CrmTaskResultAction>;

export const CrmTaskResult = z.object({
  id: Id,
  taskId: Id,
  action: CrmTaskResultAction,
  comment: z.string().nullable(),
  actedBy: Id,
  clientCreatedAt: z.iso.datetime(),
  createdAt: z.iso.datetime(),
});
export type CrmTaskResult = z.infer<typeof CrmTaskResult>;

export const CrmTask = z.object({
  id: Id,
  kind: CrmTaskKind,
  /** Код кампании-повода у `kind=patient`; пусто у поручений. */
  campaignCode: z.string().nullable(),
  title: z.string(),
  patientId: Id.nullable(),
  assigneeId: Id,
  authorId: Id,
  dueAt: z.iso.datetime(),
  state: CrmTaskState,
  clientCreatedAt: z.iso.datetime(),
  createdAt: z.iso.datetime(),
  rowVersion: z.number().int().nonnegative(),
  results: z.array(CrmTaskResult),
});
export type CrmTask = z.infer<typeof CrmTask>;

/** Постановка поручения — только `kind=internal`: CRM-повод по пациенту заводит кампания, не человек с формой. */
export const CreateCrmTaskRequest = z.object({
  title: z.string().min(1).max(500),
  assigneeId: Id,
  dueAt: z.iso.datetime(),
  /** Поручение о конкретном пациенте — необязательная привязка для контекста, не смена kind. */
  patientId: Id.optional(),
});
export type CreateCrmTaskRequest = z.infer<typeof CreateCrmTaskRequest>;

/** Ход задачи в очереди устройства — одно тело, действие названо изнутри, как переход статуса наряда. */
export const CrmTaskProgressRequest = z.object({ action: z.literal('progress'), comment: z.string().max(1000).optional() });
export const CrmTaskCompleteRequest = z.object({ action: z.literal('done'), comment: z.string().max(1000).optional() });
export const CrmTaskAcceptRequest = z.object({ action: z.literal('accepted'), comment: z.string().max(1000).optional() });
export const CrmTaskReworkRequest = z.object({ action: z.literal('rework'), comment: z.string().min(1).max(1000) });
export const CrmTaskCancelRequest = z.object({ action: z.literal('cancelled'), comment: z.string().max(1000).optional() });

/**
 * Правка поручения без смены состояния: формулировка, исполнитель, срок,
 * пациент. Поля необязательны — уходит только то, что изменили; `patientId:
 * null` снимает привязку. Ход задачи (`results`) это действие не пополняет:
 * история хранит, что с поручением делали, а не как его переформулировали.
 * Допустима только у незавершённого поручения (`open`/`in_progress`) и только
 * автору или роли с `task:assign` — исполнитель себе срок не переносит.
 */
export const CrmTaskEditRequest = z
  .object({
    action: z.literal('edit'),
    title: z.string().min(1).max(500).optional(),
    assigneeId: Id.optional(),
    dueAt: z.iso.datetime().optional(),
    patientId: Id.nullable().optional(),
  })
  .refine((change) => change.title !== undefined || change.assigneeId !== undefined || change.dueAt !== undefined || change.patientId !== undefined, {
    message: 'Нечего менять: укажите хотя бы одно поле',
  });
export type CrmTaskEditRequest = z.infer<typeof CrmTaskEditRequest>;

export const CrmTaskChange = z.discriminatedUnion('action', [
  CrmTaskProgressRequest,
  CrmTaskCompleteRequest,
  CrmTaskAcceptRequest,
  CrmTaskReworkRequest,
  CrmTaskCancelRequest,
  CrmTaskEditRequest,
]);
export type CrmTaskChange = z.infer<typeof CrmTaskChange>;

export const CrmTasksQuery = z.object({
  assigneeId: Id.optional(),
  state: CrmTaskState.optional(),
  kind: CrmTaskKind.optional(),
  overdue: z.coerce.boolean().optional(),
});
export type CrmTasksQuery = z.infer<typeof CrmTasksQuery>;

export const CrmTasks = z.object({ tasks: z.array(CrmTask) });
export type CrmTasks = z.infer<typeof CrmTasks>;

// --- Кампании -----------------------------------------------------------------

export const CAMPAIGN_CODE = [
  'appointment_reminder',
  'appointment_confirmation',
  'birthday',
  'prophylaxis',
  'patient_return',
  'feedback_request',
  'plan_stale',
  'warranty_expiring',
  'debt_reminder',
  /** Не сообщение — внутренняя автоматика «завести задачу на перезапись после неявки». */
  'no_show_reschedule',
] as const;
export const CampaignCode = z.enum(CAMPAIGN_CODE);
export type CampaignCode = z.infer<typeof CampaignCode>;

export const CampaignChannel = z.enum(['sms', 'push', 'telegram']);
export type CampaignChannel = z.infer<typeof CampaignChannel>;

export const Campaign = z.object({
  id: Id,
  code: CampaignCode,
  name: z.string(),
  channel: CampaignChannel,
  templateCode: z.string(),
  enabled: z.boolean(),
  params: z.record(z.string(), z.unknown()),
  rowVersion: z.number().int().nonnegative(),
});
export type Campaign = z.infer<typeof Campaign>;

export const Campaigns = z.object({ campaigns: z.array(Campaign) });
export type Campaigns = z.infer<typeof Campaigns>;

export const UpdateCampaignRequest = z.object({
  enabled: z.boolean().optional(),
  params: z.record(z.string(), z.unknown()).optional(),
});
export type UpdateCampaignRequest = z.infer<typeof UpdateCampaignRequest>;

export const CampaignStats = z.object({
  code: CampaignCode,
  period: z.string(),
  sentCount: z.number().int().nonnegative(),
  convertedCount: z.number().int().nonnegative(),
  conversionRate: z.number().nullable(),
});
export type CampaignStats = z.infer<typeof CampaignStats>;

// --- Шаблоны сообщений -----------------------------------------------------------

export const MessageTemplate = z.object({
  id: Id,
  code: CampaignCode,
  text: z.string(),
  rowVersion: z.number().int().nonnegative(),
});
export type MessageTemplate = z.infer<typeof MessageTemplate>;

export const MessageTemplates = z.object({ templates: z.array(MessageTemplate) });
export type MessageTemplates = z.infer<typeof MessageTemplates>;

export const UpdateMessageTemplateRequest = z.object({ text: z.string().min(1).max(1000) });
export type UpdateMessageTemplateRequest = z.infer<typeof UpdateMessageTemplateRequest>;

// --- История касаний пациента ------------------------------------------------------

export const PatientEngagement = z.object({
  id: Id,
  campaignCode: CampaignCode,
  channel: CampaignChannel,
  outcome: z.enum(['sent', 'deferred', 'suppressed']),
  createdAt: z.iso.datetime(),
});
export type PatientEngagement = z.infer<typeof PatientEngagement>;

export const PatientEngagements = z.object({ engagements: z.array(PatientEngagement) });
export type PatientEngagements = z.infer<typeof PatientEngagements>;

// --- Отзыв -------------------------------------------------------------------------

/** Публичное представление — без tenantId/patientId/appointmentId: предъявителю ссылки они не положены. */
export const PublicFeedbackRequest = z.object({
  respondedAt: z.iso.datetime().nullable(),
  expired: z.boolean(),
});
export type PublicFeedbackRequest = z.infer<typeof PublicFeedbackRequest>;

export const SubmitFeedbackRequest = z.object({
  rating: z.number().int().min(1).max(5),
  comment: z.string().max(2000).optional(),
});
export type SubmitFeedbackRequest = z.infer<typeof SubmitFeedbackRequest>;
